import { z } from "zod";
import { Key } from "./definitions.js";
const Id = z.number().int().positive();
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD");
// ---------- containers ----------
export const ContainerKey = z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z][a-z0-9_-]*$/, "container key must be lower-case letters, digits, underscores or hyphens");
export const Container = z.object({
    id: Id,
    key: ContainerKey,
    name: z.string(),
    description: z.string(),
    definitionId: Id,
    status: z.enum(["active", "archived"]),
    isTemplate: z.boolean(),
    createdFromTemplateId: Id.nullable(),
    /** the project's owner (PM); defaults to the creator */
    ownerId: Id.nullable(),
    plannedStart: IsoDate.nullable(),
    plannedEnd: IsoDate.nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/** What an agent or a person asking "what projects do I have?" needs: one line per project. */
export const ProjectSummary = z.object({
    id: Id,
    key: ContainerKey,
    name: z.string(),
    definitionKey: z.string(),
    status: z.enum(["active", "archived"]),
    owner: z.object({ id: Id, name: z.string(), email: z.string() }).nullable(),
    projectAdmins: z.array(z.object({ id: Id, name: z.string() })),
    plannedStart: IsoDate.nullable(),
    plannedEnd: IsoDate.nullable(),
    /** "container" when set on the project, "objects" when derived from its objects' planned dates */
    datesFrom: z.enum(["container", "objects", "none"]),
    objectCount: z.number().int(),
});
export const CreateContainerInput = z.object({
    key: ContainerKey,
    name: z.string().min(1).max(200),
    description: z.string().max(5000).default(""),
    /** required unless fromTemplateId is given (then taken from the template) */
    definitionId: Id.optional(),
    /** copy this template container's tree into the new container (PRD 5.3) */
    fromTemplateId: Id.optional(),
    /** shift planned dates so the earliest template date lands here; omit to clear planned dates */
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    keepOwners: z.boolean().default(false),
    copyAcl: z.boolean().default(true),
    /** owner (PM); defaults to the caller */
    ownerId: Id.optional(),
    plannedStart: IsoDate.nullable().optional(),
    plannedEnd: IsoDate.nullable().optional(),
});
export const SetTemplateInput = z.object({ isTemplate: z.boolean() });
/** Template drift: what a project and its template no longer have in common (design 3.6). */
export const TemplateDriftRef = z.object({ id: Id, name: z.string(), typeKey: z.string(), parentId: Id.nullable(), templateObjectId: Id.nullable() });
export const TemplateDrift = z.object({
    containerId: Id,
    templateId: Id,
    /** live project objects still linked to a live template object */
    matched: z.number().int(),
    /** template objects with no counterpart in the project */
    missing: z.array(TemplateDriftRef),
    /** project objects with no template source (added later, or source removed from the template) */
    extra: z.array(TemplateDriftRef),
});
export const UpdateContainerInput = z
    .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(5000).optional(),
    status: z.enum(["active", "archived"]).optional(),
    ownerId: Id.nullable().optional(),
    plannedStart: IsoDate.nullable().optional(),
    plannedEnd: IsoDate.nullable().optional(),
    /** with a new plannedStart: move every period, cadence and planned date in the project by the same number of days */
    shiftDates: z.boolean().optional(),
})
    .strict();
// ---------- work objects ----------
export const WorkObject = z.object({
    id: Id,
    containerId: Id,
    typeId: Id,
    parentId: Id.nullable(),
    /** ancestor ids from root to parent */
    ancestorIds: z.array(Id),
    name: z.string(),
    description: z.string(),
    stateId: Id,
    plannedStart: IsoDate.nullable(),
    plannedEnd: IsoDate.nullable(),
    actualStart: IsoDate.nullable(),
    actualEnd: IsoDate.nullable(),
    estimate: z.number().nullable(),
    ownerId: Id.nullable(),
    assigneeIds: z.array(Id),
    labels: z.array(z.string()),
    custom: z.record(z.string(), z.unknown()),
    aiSummary: z.string().nullable(),
    /** the period the object is allocated to (design 3.7) */
    timeboxId: Id.nullable(),
    restricted: z.boolean(),
    sequence: z.number().int(),
    version: z.number().int(),
    deletedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    createdBy: Id,
    updatedAt: z.coerce.date(),
    updatedBy: Id,
});
/** Property names a caller may ask for with `fields` (sparse fieldsets; `id` always comes back). */
export const ObjectField = WorkObject.keyof();
/** `?fields=id,name,stateId` */
export const FieldsParam = z
    .string()
    .max(500)
    .transform((s) => s.split(",").map((f) => f.trim()).filter(Boolean))
    .pipe(z.array(ObjectField).min(1));
/** An object cut down to the requested fields. */
export const SparseWorkObject = WorkObject.partial().required({ id: true });
/** What reads return: the whole object, or the fields asked for. */
export const WorkObjectOrSparse = z.union([WorkObject, SparseWorkObject]);
const Label = z.string().min(1).max(60);
export const CreateObjectInput = z.object({
    containerId: Id,
    typeId: Id,
    parentId: Id.nullable().default(null),
    name: z.string().min(1).max(500),
    description: z.string().max(100_000).default(""),
    /** defaults to the type's initial state */
    stateId: Id.optional(),
    plannedStart: IsoDate.nullable().optional(),
    plannedEnd: IsoDate.nullable().optional(),
    actualStart: IsoDate.nullable().optional(),
    actualEnd: IsoDate.nullable().optional(),
    estimate: z.number().nonnegative().nullable().optional(),
    ownerId: Id.nullable().optional(),
    assigneeIds: z.array(Id).max(50).default([]),
    labels: z.array(Label).max(50).default([]),
    custom: z.record(z.string(), z.unknown()).default({}),
    sequence: z.number().int().optional(),
    timeboxId: Id.nullable().optional(),
});
/** Partial update. `custom` is merged key by key; a null value removes the key. */
export const UpdateObjectInput = z
    .object({
    version: z.number().int().positive().optional(),
    name: z.string().min(1).max(500).optional(),
    description: z.string().max(100_000).optional(),
    plannedStart: IsoDate.nullable().optional(),
    plannedEnd: IsoDate.nullable().optional(),
    actualStart: IsoDate.nullable().optional(),
    actualEnd: IsoDate.nullable().optional(),
    estimate: z.number().nonnegative().nullable().optional(),
    ownerId: Id.nullable().optional(),
    assigneeIds: z.array(Id).max(50).optional(),
    labels: z.array(Label).max(50).optional(),
    custom: z.record(z.string(), z.unknown()).optional(),
    aiSummary: z.string().max(20_000).nullable().optional(),
    sequence: z.number().int().optional(),
    timeboxId: Id.nullable().optional(),
})
    .strict();
export const TransitionObjectInput = z.object({ stateId: Id, version: z.number().int().positive().optional() });
export const MoveObjectInput = z.object({
    parentId: Id.nullable(),
    /** omit to stay in the current container */
    containerId: Id.optional(),
    version: z.number().int().positive().optional(),
});
export const ListObjectsQuery = z.object({
    container_id: z.coerce.number().int().positive().optional(),
    parent_id: z.coerce.number().int().positive().optional(),
    /** "true": only root objects (parent null) */
    root: z.enum(["true", "false"]).optional(),
    type_id: z.coerce.number().int().positive().optional(),
    state_id: z.coerce.number().int().positive().optional(),
    state_category: z.enum(["todo", "in_progress", "done", "cancelled"]).optional(),
    owner_id: z.coerce.number().int().positive().optional(),
    timebox_id: z.coerce.number().int().positive().optional(),
    label: Label.optional(),
    include_deleted: z.enum(["true", "false"]).optional(),
    cursor: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    fields: FieldsParam.optional(),
});
export const ObjectPage = z.object({
    items: z.array(WorkObjectOrSparse),
    nextCursor: Id.nullable(),
});
export const ActivityEntry = z.object({
    id: Id,
    at: z.coerce.date(),
    actorKind: z.string(),
    actorId: Id.nullable(),
    entityKind: z.string(),
    /** 0 on project-admin grants recorded at container creation before 2 Nov 2026 (activity is append-only) */
    entityId: z.number().int().nonnegative(),
    objectId: Id.nullable(),
    action: z.string(),
    changes: z.record(z.string(), z.unknown()),
    batchId: z.uuid(),
    flags: z.array(z.string()),
    revertsActivityId: Id.nullable(),
    revertedByActivityId: Id.nullable(),
});
export { Key as ObjectKey };
export const HistoryQuerySchema = z.object({
    field: z.string().min(1).max(100).optional(),
    since: z.coerce.date().optional(),
    until: z.coerce.date().optional(),
    before: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
/** GET /activity: the workspace feed. `actor` is `user:12` or `service_account:3`. */
export const ActivityFeedQuery = z.object({
    since: z.coerce.date().optional(),
    until: z.coerce.date().optional(),
    /** one kind or a comma list, e.g. `service_account,api_key` */
    entity_kind: z.string().regex(/^[a-z_]+(,[a-z_]+)*$/).max(200).optional(),
    batch_id: z.uuid().optional(),
    actor: z.string().regex(/^(user|service_account):\d+$/).optional(),
    before: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const FieldDiff = z.object({ field: z.string(), from: z.unknown(), to: z.unknown(), textDiff: z.string().optional() });
export const ObjectDiff = z.object({ from: WorkObject, to: WorkObject, changes: z.array(FieldDiff) });
export const RevertResult = z.object({ reverted: ActivityEntry, by: z.array(Id) });
export const BatchRevertResult = z.object({ reverted: z.array(Id), skipped: z.array(Id) });
export const UndoResult = z.object({ reverted: z.array(Id) });
// ---------- query language (design 6.3) ----------
export const FilterOp = z.enum(["eq", "neq", "in", "nin", "lt", "lte", "gt", "gte", "contains", "is_null", "like"]);
const Scalar = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const FilterCondition = z.object({
    field: z.string().min(1).max(100),
    op: FilterOp,
    value: z.union([Scalar, z.array(Scalar)]).optional(),
});
export const FilterNode = z.lazy(() => z.union([FilterCondition, z.object({ and: z.array(FilterNode).min(1) }), z.object({ or: z.array(FilterNode).min(1) }), z.object({ not: FilterNode })]));
export const OrderBy = z.object({ field: z.string().min(1).max(100), dir: z.enum(["asc", "desc"]).default("asc") });
export const ObjectQuery = z.object({
    where: FilterNode.optional(),
    order_by: z.array(OrderBy).max(1).default([]),
    limit: z.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
    include_deleted: z.boolean().default(false),
    /** sparse fieldset: only these properties (plus id) per object */
    fields: z.array(ObjectField).min(1).max(30).optional(),
});
export const ObjectQueryPage = z.object({ items: z.array(WorkObjectOrSparse), nextCursor: z.string().nullable() });
/** Lexical search (trigram on name, substring on name/description) under the caller's visibility. */
export const SearchMode = z.enum(["lexical", "semantic", "hybrid"]);
export const SearchInput = z.object({
    text: z.string().trim().min(2).max(500),
    containerId: Id.optional(),
    limit: z.number().int().min(1).max(100).default(20),
    /** default: hybrid when an embedding provider is configured, otherwise lexical */
    mode: SearchMode.optional(),
});
export const SearchHit = z.object({ object: WorkObject, score: z.number(), matched: z.array(z.enum(["lexical", "semantic"])) });
// ---------- bulk ----------
export const BulkOp = z.discriminatedUnion("op", [
    z.object({ op: z.literal("create"), body: CreateObjectInput }),
    z.object({ op: z.literal("update"), id: Id, body: UpdateObjectInput }),
    z.object({ op: z.literal("transition"), id: Id, body: TransitionObjectInput }),
    z.object({ op: z.literal("move"), id: Id, body: MoveObjectInput }),
    z.object({ op: z.literal("delete"), id: Id }),
    z.object({ op: z.literal("restore"), id: Id }),
]);
export const BulkInput = z.object({
    ops: z.array(BulkOp).min(1).max(500),
    /** true (default): all or nothing. false: each op in its own savepoint, failures reported per item */
    atomic: z.boolean().default(true),
});
export const BulkItemResult = z.object({
    index: z.number().int(),
    ok: z.boolean(),
    result: z.unknown().optional(),
    error: z.object({ code: z.string(), detail: z.string() }).optional(),
});
export const BulkResult = z.object({ results: z.array(BulkItemResult), succeeded: z.number().int(), failed: z.number().int() });
// ---------- relations ----------
export const RelationKind = z.enum(["blocks", "relates_to", "duplicates", "depends_on", "migrated_from", "created_from_template"]);
/** Inverse names, for display from the target's side. */
export const INVERSE_RELATION = {
    blocks: "is_blocked_by",
    relates_to: "relates_to",
    duplicates: "is_duplicated_by",
    depends_on: "is_depended_on_by",
    migrated_from: "migrated_to",
    created_from_template: "template_for",
};
export const RelationTarget = z.object({ id: Id, visible: z.boolean(), name: z.string().optional(), typeId: Id.optional(), stateId: Id.optional(), containerId: Id.optional() });
export const ObjectRelations = z.object({
    outgoing: z.array(z.object({ kind: RelationKind, target: RelationTarget, createdAt: z.coerce.date() })),
    incoming: z.array(z.object({ kind: RelationKind, inverse: z.string(), source: RelationTarget, createdAt: z.coerce.date() })),
});
// ---------- comments & attachments ----------
export const Comment = z.object({
    id: Id,
    objectId: Id,
    authorId: Id,
    body: z.string(),
    createdAt: z.coerce.date(),
    editedAt: z.coerce.date().nullable(),
    deletedAt: z.coerce.date().nullable(),
});
export const CreateCommentInput = z.object({ body: z.string().min(1).max(100_000) });
export const UpdateCommentInput = z.object({ body: z.string().min(1).max(100_000) });
export const Attachment = z.object({
    id: Id,
    objectId: Id,
    name: z.string(),
    mime: z.string(),
    size: z.number().int().nonnegative(),
    urlOrKey: z.string(),
    createdAt: z.coerce.date(),
    createdBy: Id,
});
export const CreateAttachmentInput = z.object({ name: z.string().min(1).max(500), mime: z.string().min(1).max(200), size: z.number().int().nonnegative(), urlOrKey: z.string().min(1).max(4000) });
//# sourceMappingURL=objects.js.map